import { QuestionnaireScoringConfig } from './custom-questionnaire-scoring.model';

export const deqQuestionnaireScoringConfig: QuestionnaireScoringConfig = {
  questionnaireType: 'DEQ',
  scoringCalculator: (formGroup: JSON, questionnaireKeys: string[]) => {
    let score = 0;
    questionnaireKeys.forEach(key => {
      score += formGroup[key];
    });
    return score;
  },
  questionnaireKeys: [
    'eyeDiscomfortFrequency',
    'eyeDiscomfortIntensity',
    'eyeDrynessFrequency',
    'eyeDrynessIntensity',
    'wateryEyes'
  ]
};

export const speedQuestionnaireScoringConfig: QuestionnaireScoringConfig = {
  questionnaireType: 'SPEED',
  scoringCalculator: (formGroup: JSON, questionnaireKeys: string[]) => {
    let score = 0;
    questionnaireKeys.forEach(key => {
      score += formGroup[key];
    });
    return score;
  },
  questionnaireKeys: [
    'drynessGrittinessScratchinessFrequency',
    'sorenessIrritationFrequency',
    'burningWateringFrequency',
    'eyeFatigueFrequency',
    'drynessGrittinessScratchinessSeverity',
    'sorenessIrritationSeverity',
    'burningWateringSeverity',
    'eyeFatigueSeverity'
  ]
};

export const speedIIQuestionnaireScoringConfig: QuestionnaireScoringConfig = {
  questionnaireType: 'SPEEDII',
  scoringCalculator: (formGroup: JSON, questionnaireKeys: string[]) => {
    let score = 0;
    const defaultScores = {
      yes: 1,
      sometimes: 1,
      frequently: 1,
      always: 1,
      today: 1,
      threeDays: 1,
      threeMonths: 1,
      eyeLidRedness: 1,
      crusting: 1,
      lidIrritation: 1,
      no: 0,
      never: 0
    };

    const optionalQuestionnaireKeys = [
      'fluctuatingVisionProblems-optional',
      'contactLens-optional2',
      'eyeItch-optional',
      'glasses-optional'
    ];

    const calculateScore = key => {
      if (
        Object.keys(defaultScores).includes(formGroup[key]) &&
        typeof formGroup[key] != 'object'
      ) {
        return defaultScores[formGroup[key]];
      } else if (typeof formGroup[key] === 'object' && formGroup[key] !== null) {
        let score = 0;
        Object.keys(formGroup[key]).forEach(innerKey => {
          if (formGroup[key][innerKey]) {
            score++;
          }
        });
        return score;
      }
      return formGroup[key];
    };

    questionnaireKeys.forEach(key => {
      score += calculateScore(key);
    });

    optionalQuestionnaireKeys.forEach(key => {
      score += calculateScore(key) || 0;
    });
    return score;
  },
  questionnaireKeys: [
    'drynessGrittinessScratchinessFrequency',
    'sorenessIrritationFrequency',
    'burningWateringFrequency',
    'eyeFatigueFrequency',
    'drynessGrittinessScratchinessSeverity',
    'sorenessIrritationSeverity',
    'burningWateringSeverity',
    'eyeFatigueSeverity',
    // SPEED II questions
    'symptomsExperienced',
    'usesEyeDropOintment',
    'fluctuatingVisionProblems',
    'hadBlepharitis',
    'hadStye',
    'otherSymptoms',
    'contactLens',
    'eyeItch',
    'symptomsSymmetric',
    'glasses'
  ]
};

const totalOSDIQuestions = 12;

export const osdiQuestionnaireScoringConfig: QuestionnaireScoringConfig = {
  questionnaireType: 'OSDI',
  scoringCalculator: (formGroup: JSON, questionnaireKeys: string[]) => {
    let numberOfQuestionsAnswered = 0;
    let sumOfAnswers = 0;
    let totalQuestions = 0;

    questionnaireKeys.forEach(key => {
      totalQuestions++;
      // Anything below 0 is Not Applicable
      if (formGroup[key] >= 0) {
        numberOfQuestionsAnswered++;
        sumOfAnswers += formGroup[key];
      }
    });
    if (totalQuestions !== totalOSDIQuestions) {
      throw new Error(
        `Number of OSDI Questions (${totalQuestions}) do no match total number of expected questions for OSDI (${totalOSDIQuestions}).`
      );
    }
    const score = (sumOfAnswers * 100) / (numberOfQuestionsAnswered * 4);
    return Math.round(score);
  },
  questionnaireKeys: [
    'lightSensitivity',
    'grittyFeeling',
    'painOrSoreness',
    'blurredVision',
    'poorVision',
    'reading',
    'drivingAtNight',
    'workingWithScreens',
    'watchingTV',
    'windyConditions',
    'lowHumidity',
    'airConditioned'
  ]
};

export const osdi6QuestionnaireScoringConfig: QuestionnaireScoringConfig = {
  questionnaireType: 'OSDI6',
  scoringCalculator: (formGroup: JSON, questionnaireKeys: string[]) => {
    let score = 0;
    questionnaireKeys.forEach(key => {
      score += formGroup[key];
    });
    return score;
  },
  questionnaireKeys: [
    'lightSensitivityOSDI6',
    'blurredVisionOSDI6',
    'drivingAtNightOSDI6',
    'watchingTVOSDI6',
    'windyConditionsOSDI6',
    'lowHumidityOSDI6'
  ]
};

export const fitzpatrickQuestionnaireScoringConfig: QuestionnaireScoringConfig = {
  questionnaireType: 'FITZPATRICK',
  scoringCalculator: (formGroup: JSON, questionnaireKeys: string[]) => {
    let score = 0;
    questionnaireKeys.forEach(key => {
      score += fitzpatrickKeyToScoreMap[formGroup[key]];
    });
    return score;
  },
  questionnaireKeys: [
    'eyeColor',
    'naturalHairColor',
    'skinColor',
    'freckles',
    'longSunExposure',
    'darkerAfterSunExposure',
    'tanIntensity',
    'faceSunSensitivity',
    'sunTanExposureFrequency',
    'lastSunExposure'
  ]
};

export const fitzpatrickKeyToScoreMap = {
  hairColorRedhead: 0,
  hairColorBlond: 1,
  hairColorBrownDarkBlond: 2,
  hairColorDarkBrowns: 3,
  hairColorBlack: 4,

  pink: 0,
  veryPale: 1,
  paleBrownOlive: 2,
  brown: 3,
  darkBrunette: 4,

  bluesGreensPaleGrays: 0,
  bluesGreensGrays: 1,
  bluesGreensDarkGraysPers: 2,
  darkBrowns: 3,
  brownishBlacks: 4,

  aLot: 0,
  several: 1,
  some: 2,
  veryLittle: 3,
  none: 4,

  severeBurns: 0,
  moderateBurns: 1,
  sometimesBurning: 2,
  rareBurns: 3,
  noBurns: 4,

  never: 0,
  rarely: 1,
  sometimes: 2,
  often: 3,
  always: 4,

  slightNone: 0,
  lightTan: 1,
  mediumTan: 2,
  darkTan: 3,
  veryDarkTan: 4,

  verySensitive: 0,
  sensitive: 1,
  moderatelySensitive: 2,
  resistant: 3,
  veryResistant: 4,

  neverTan: 0,
  rarelyTan: 1,
  sometimesTan: 2,
  oftenTan: 3,
  alwaysTan: 4,

  moreThanThreeMonths: 0,
  lastTwoToThreeMonths: 1,
  lastOneToTwoMonths: 2,
  lastWeek: 3,
  lastDay: 4
};

export const allQuestionnairesScoringConfig: QuestionnaireScoringConfig[] = [
  deqQuestionnaireScoringConfig,
  speedQuestionnaireScoringConfig,
  speedIIQuestionnaireScoringConfig,
  osdiQuestionnaireScoringConfig,
  fitzpatrickQuestionnaireScoringConfig,
  osdi6QuestionnaireScoringConfig
];
